import React, {
  Reducer, useCallback, useEffect, useReducer, useRef, useState,
} from 'react';
import {
  Alert, Button, Card, Icon, Label, LinkAsButton, Spinner, TextInput, useMediaQuery,
} from '@printdeal/ui-library';
import { useChat } from 'ai/react';
import cx from 'clsx';
import { FEATURE_MANAGEMENT_USER_ID_KEY } from '@printdeal/feature-management/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import { getFromLocalStorage, LS_KEYS } from '../../../helpers/window';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import ContentfulCardImage from '../../ContentfulCardImage';

export interface RelatedProduct {
  id: string;
  title: string;
  image: string;
  slug: string;
}

type Message = {
  id: string;
  type?: 'products' | 'faq';
  role: 'user' | 'assistant';
  content: string;
  metadata?: RelatedProduct[];
};

interface Props {
  isChatting: boolean;
  setRelatedProducts: (products: RelatedProduct[]) => void;
  setIsChatting: (isChatting: boolean) => void;
}

interface ChatState {
  lastMessageReceived: boolean;
  messageSent: boolean;
  loading: boolean;
  error: boolean;
}

type NewChatState = Partial<ChatState>;
type ChatReducer = Reducer<ChatState, NewChatState>;

const initialState: ChatState = {
  lastMessageReceived: false,
  messageSent: false,
  loading: false,
  error: false,
};

const chatReducer: ChatReducer = (state, newState) => ({
  ...state,
  ...newState,
});

export const HeroSearchChat: React.FC<Props> = ({ isChatting, setRelatedProducts, setIsChatting }) => {
  const intl = useIntl();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const userId = getFromLocalStorage(FEATURE_MANAGEMENT_USER_ID_KEY);
  const authToken = getFromLocalStorage(LS_KEYS.AUTH_TOKEN);
  const formRef = useRef<HTMLFormElement>(null);
  const chatboxRef = useRef<HTMLOListElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [chat, setChat] = useReducer<ChatReducer>(chatReducer, initialState);
  const [messages, setMessages] = useState<Message[]>([]);

  const suggestions = [
    intl.formatMessage({
      id: 'homepage.ai.hero.chat.suggestion1',
      defaultMessage: 'Do you have baby products?',
    }),
    intl.formatMessage({
      id: 'homepage.ai.hero.chat.suggestion2',
      defaultMessage: 'Do you have sustainable t-shirts?',
    }),
  ];

  const {
    input, setInput, handleInputChange, handleSubmit, isLoading,
  } = useChat({
    api: `${process.env.GATSBY_PRODUCT_AI_ENDPOINT}/guided-search`,
    id: userId,
    headers: {
      Authorization: authToken as string,
    },
    body: {
      userId,
    },
    onResponse: async (response) => {
      if (!response.ok) {
        setChat({ error: true, loading: false });
        setMessages((prev) => [...prev, {
          id: `assistant-${new Date().getTime()}`,
          role: 'assistant',
          content: intl.formatMessage({
            id: 'homepage.ai.hero.chat.error',
            defaultMessage: 'I am sorry, I could not process your request. Please try again later.',
          }),
        }]);
        return;
      }

      const body = await response.json() as { messages: Message[] };
      const products = body.messages[body.messages.length - 1].metadata;
      if (products) {
        setRelatedProducts(products as RelatedProduct[]);
      }

      setMessages((prev) => [...prev, ...body.messages]);
      setChat({ loading: false, lastMessageReceived: true, messageSent: false });

      dispatchDataLayerEntry(
        'receive_chat_message',
        'display',
        {
          experiment: 'EXP1530',
        },
      );
    },
  });

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollHeight, clientHeight } = messagesContainerRef.current;
      messagesContainerRef.current.scrollTo({
        top: scrollHeight - clientHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100); // Delay scrolling to ensure content has rendered

    return () => clearTimeout(timer);
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    if (isChatting) {
      scrollToBottom();
    }
  }, [isChatting, scrollToBottom]);

  const handleChat = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!input || isLoading) {
      return;
    }

    setChat({ messageSent: true, error: false, lastMessageReceived: false });
    setMessages((prev) => [...prev, {
      id: `user-${new Date().getTime()}`,
      role: 'user',
      content: input,
    }]);

    setIsChatting(true);
    handleSubmit(e);
  };

  return (
    <section>
      {chat.error && (
        <Alert variant="error">
          {intl.formatMessage({
            id: 'homepage.ai.hero.chat.error',
            defaultMessage: 'I am sorry, I could not process your request. Please try again later.',
          })}
        </Alert>
      )}
      <div
        id="messages-container"
        ref={messagesContainerRef}
        className={cx(
          'pd-grid pd-items-end pd-gap-3 pd-overflow-auto pd-transition-all pd-duration-300',
          {
            'pd-min-h-[15px]': !isChatting,
            'pd-min-h-[370px] pd-max-h-[370px] pd-pt-[10px]': isChatting,
          },
        )}
      >
        <ol
          id="chatbox"
          ref={chatboxRef}
          className="pd-grid pd-snap-y pd-snap-mandatory pd-gap-3 pd-overflow-visible pd-scroll-smooth pd-py-2 pd-pr-1"
        >
          {messages.map((message) => (
            <React.Fragment key={message.id}>
              <li
                className={cx('pd-py-2 pd-px-5 pd-rounded pd-lg:max-w-[80%] pd-shadow-sm', {
                  'pd-bg-white pd-border-2 pd-justify-self-end': message.role === 'user',
                  'pd-bg-primary-500 pd-justify-self-start': message.role === 'assistant',
                })}
              >
                <span className="pd-block pd-text-sm">
                  {message.role === 'user' ? (
                    <FormattedMessage
                      id="homepage.ai.hero.chat.user"
                      defaultMessage="You:"
                    />
                  ) : (
                    <FormattedMessage
                      id="homepage.ai.hero.chat.assistant"
                      defaultMessage="Assistant:"
                    />
                  )}
                </span>
                <p>{message.content}</p>
              </li>
              <div className={cx('pd-grid pd-gap-4 lg:pd-hidden', {
                'pd-hidden': !message.metadata,
                'pd-grid-cols-3': !isMobile,
                'pd-grid-cols-2': isMobile,
              })}
              >
                {message.metadata?.slice(0, isMobile ? 2 : 3)?.map((product: RelatedProduct) => (
                  <Card
                    key={product.id}
                    image={(
                      <ContentfulCardImage
                        src={product.image}
                        priority
                        alt={product.title}
                      />
                    )}
                    title={product.title}
                    link={<GatsbyLink to={product.slug} />}
                    // eslint-disable-next-line tailwindcss/no-custom-classname
                    className="pd-animation-fade-in-scale pd-h-full pd-cursor-pointer"
                    variant="square"
                    size="sm"
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
          {isLoading && (
            <div className="pd-py-3">
              <Spinner variant="primary" screenReaderLabel="Loading..." />
            </div>
          )}
        </ol>
      </div>
      <form onSubmit={handleChat} className="pd-my-2 pd-flex pd-w-full pd-flex-col pd-gap-2 pd-pb-4" ref={formRef}>
        <Label htmlFor="ai-question" className="pd-w-full">
          <FormattedMessage
            id="homepage.ai.hero.chat.label"
            defaultMessage="Ask me anything"
          />
        </Label>
        <TextInput
          name="ai-question"
          id="ai-question"
          variant="primary"
          className="pd-w-full"
          onChange={handleInputChange}
          value={input}
          placeholder={intl.formatMessage({
            id: 'homepage.ai.hero.chat.placeholder',
            defaultMessage: 'Do you have sustainable t-shirts?',
          })}
          disabled={isLoading}
          iconAfter={(
            <LinkAsButton
              as="button"
              variant="secondary"
              type="submit"
              iconBefore={isLoading ? <Spinner variant="primary" /> : <Icon icon="sparkles" className="pd-text-primary-600" />}
            />
          )}
        />
        {!messages.length && (
          <div className="pd-mt-2 pd-flex pd-w-full pd-flex-col pd-gap-2 sm:pd-flex-row">
            {suggestions.map((suggestion) => (
              <Button
                key={suggestion}
                variant="tertiary"
                type="submit"
                onClick={() => setInput(suggestion)}
                className="pd-w-full pd-justify-start pd-px-3 pd-py-2 pd-text-xs sm:pd-w-1/2"
                iconAfter="arrow-up"
              >
                {suggestion}
              </Button>
            ))}
          </div>
        )}
      </form>
    </section>
  );
};
